import { Badge } from "@/components/Badge";
import { DownloadButton } from "@/components/DownloadButton";
import { ExportStatus } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDate, parseDateWithHours } from "@/lib/utils";
import { CheckIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

export type TDownloadCenter = {
  id: number;
  user_name: string;
  client: string;
  created_at: string;
  reporting_date?: string;
  status: string;
  shortcuts: boolean;
};

export const getDownloadCenterColumns = (
  t: TFunction,
): ColumnDef<TDownloadCenter>[] => [
  {
    accessorKey: "id",
    header: t("technical_info.id", { defaultValue: "ID" }),
  },
  {
    accessorKey: "reporting_date",
    header: t("download_center.columns.reporting_date", {
      defaultValue: "Reporting date",
    }),
    cell: ({ row }) => {
      const value = row.getValue("reporting_date") as string | undefined;
      return value ? parseDate(value) : "";
    },
  },
  {
    accessorKey: "shortcuts",
    header: t("download_center.columns.shortcuts", {
      defaultValue: "Shortcuts",
    }),
    cell: ({ getValue }) =>
      getValue() ? (
        <div className="pl-5">
          <CheckIcon />
        </div>
      ) : null,
  },
  {
    accessorKey: "user_name",
    header: t("download_center.columns.user_name", {
      defaultValue: "User",
    }),
  },
  {
    accessorKey: "created_at",
    header: t("download_center.columns.created_at", {
      defaultValue: "Generated at",
    }),
    cell: ({ row }) => {
      const value = row.getValue("created_at") as string | undefined;
      return value ? parseDateWithHours(value) : "";
    },
  },
  {
    accessorKey: "status",
    header: t("download_center.columns.status", {
      defaultValue: "Status",
    }),
    cell: ({ getValue }) => {
      const value = getValue() as ExportStatus;
      const badgeColor = statusToBadgeColor[value] ?? "error";
      return <Badge color={badgeColor} />;
    },
  },
  {
    id: "download",
    header: t("actions.download", { defaultValue: "Download" }),
    cell: ({ row }) => {
      const id = row.getValue("id") as number;

      const href = `${import.meta.env.VITE_BACKEND_URL}/a7/a7_opcvm_download_center/download/${id}`;

      return (
        <DownloadButton
          href={href}
          size="sm"
          variant="light"
          color="primary"
          isIconOnly
        />
      );
    },
  },
];
